import type { FC } from "react";

import { assertUnreachable } from "@arti/utilities";

import type { QuotaForecastingEntry } from "../../types/quotaForecasting";

import { formatTokenCount, formatUsd } from "../../utils/currency";
import { formatDateTime } from "../../utils/time";
import { DataTable } from "../common/DataTable";
import { EntriesTableShell } from "../common/EntriesTableShell";

const EMPTY = "\u2014";

function formatStatus(status: QuotaForecastingEntry["status"]): string {
  switch (status) {
    case "success": {
      return "Success";
    }
    case "error": {
      return "Failed";
    }
    case "throttled": {
      return "Throttled";
    }
    default: {
      throw assertUnreachable(status);
    }
  }
}

export interface QuotaForecastingEntriesTableProps {
  entries: QuotaForecastingEntry[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  errorMessage?: string | null;
  onPageChange: (page: number) => void;
}

export const QuotaForecastingEntriesTable: FC<
  QuotaForecastingEntriesTableProps
> = ({
  entries,
  total,
  page,
  pageSize,
  isLoading,
  errorMessage,
  onPageChange,
}) => (
  <EntriesTableShell
    total={total}
    page={page}
    pageSize={pageSize}
    isLoading={isLoading && entries.length === 0}
    errorMessage={errorMessage}
    isEmpty={total === 0 && entries.length === 0 && !isLoading}
    emptyTitle="No quota telemetry entries"
    emptyDescription="Try expanding the range or clearing the provider/model filters."
    onPageChange={onPageChange}
  >
    <DataTable responsive className="text-xs">
      <DataTable.Header>
        <tr>
          <DataTable.HeaderCell>Timestamp</DataTable.HeaderCell>
          <DataTable.HeaderCell>Status</DataTable.HeaderCell>
          <DataTable.HeaderCell>Total</DataTable.HeaderCell>
          <DataTable.HeaderCell>Input</DataTable.HeaderCell>
          <DataTable.HeaderCell>Output</DataTable.HeaderCell>
          <DataTable.HeaderCell>Context</DataTable.HeaderCell>
          <DataTable.HeaderCell>Budget %</DataTable.HeaderCell>
          <DataTable.HeaderCell>HTTP</DataTable.HeaderCell>
          <DataTable.HeaderCell>Error</DataTable.HeaderCell>
          <DataTable.HeaderCell>Cached</DataTable.HeaderCell>
          <DataTable.HeaderCell>Cost</DataTable.HeaderCell>
        </tr>
      </DataTable.Header>
      <DataTable.Body>
        {entries.map((entry) => (
          <DataTable.Row key={entry.id}>
            <DataTable.Cell className="whitespace-nowrap text-gray-500 dark:text-white/60">
              {formatDateTime(entry.timestamp_ms)}
            </DataTable.Cell>
            <DataTable.Cell className="whitespace-nowrap">
              {formatStatus(entry.status)}
            </DataTable.Cell>
            <DataTable.Cell className="font-mono text-right">
              {formatTokenCount(entry.total_tokens)}
            </DataTable.Cell>
            <DataTable.Cell className="font-mono text-right">
              {formatTokenCount(entry.input_tokens)}
            </DataTable.Cell>
            <DataTable.Cell className="font-mono text-right">
              {formatTokenCount(entry.output_tokens)}
            </DataTable.Cell>
            <DataTable.Cell className="font-mono text-right">
              {entry.context_window_size === null
                ? EMPTY
                : formatTokenCount(entry.context_window_size)}
            </DataTable.Cell>
            <DataTable.Cell className="font-mono text-right">
              {entry.budget_percent_used === null
                ? EMPTY
                : `${entry.budget_percent_used.toFixed(1)}%`}
            </DataTable.Cell>
            <DataTable.Cell className="font-mono text-right">
              {entry.error_status_code?.toString() ?? EMPTY}
            </DataTable.Cell>
            <DataTable.Cell className="max-w-xs truncate text-gray-500 dark:text-white/60">
              {entry.error_code && entry.error_message
                ? `${entry.error_code}: ${entry.error_message}`
                : (entry.error_code ?? entry.error_message ?? EMPTY)}
            </DataTable.Cell>
            <DataTable.Cell className="font-mono text-right">
              {formatTokenCount(entry.cached_input_tokens)}
            </DataTable.Cell>
            <DataTable.Cell className="font-mono text-right">
              {formatUsd(entry.estimated_cost_usd)}
            </DataTable.Cell>
          </DataTable.Row>
        ))}
      </DataTable.Body>
    </DataTable>
  </EntriesTableShell>
);
